// src/app/features/dashboard/services/dashboard-stats-store.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs';
import { DashboardService } from 'src/app/features/dashboard/services/dashboard.service';
import { SessionScopedState } from './session/session-scoped-state';

export interface DashboardStatItem {
  id: string;
  label: string;
  count: number;
}
export type Stats = {
  liveTests: number;
  totalPatients: number;
  testsToday: number;
  deviceInInventory: number;
  deviceInInventoryLabel: string;
  nightTechnicians: number;
};

export type Alerts = {
  message: string;
};

type AppStatsStore = {
  isLoading: boolean;
  isRefreshing: boolean;
  alerts: Alerts[];
  stats: Stats;
  departmentId: number | null;
};
const DEFAULT_VALUE = {
  isLoading: false,
  isRefreshing: false,
  alerts: [],
  stats: {
    liveTests: 0,
    totalPatients: 0,
    testsToday: 0,
    deviceInInventory: 0,
    deviceInInventoryLabel: '',
    nightTechnicians: 0,
  },
    departmentId: null,
};

export interface DashboardStatsResponse {
  alerts: { message: string; visible: boolean }[];
  stats: DashboardStatItem[];
}

@Injectable({
  providedIn: 'root',
})
export class AppStatsStoreStoreService implements SessionScopedState {
  constructor(private dashboardService: DashboardService) {}

  private statsSubject = new BehaviorSubject<AppStatsStore>(DEFAULT_VALUE);
  public stats$: Observable<AppStatsStore> = this.statsSubject.asObservable();

  public departmentId$: Observable<number | null> = this.stats$.pipe(
    map((state) => state.departmentId),
    distinctUntilChanged()
  );

  /**
   * Back to the empty state, including the department the figures were scoped
   * to — that is the signed-in user's reach, and it is the one field here that
   * would quietly narrow or widen the next user's dashboard.
   */
  public clearSessionState(): void {
    this.statsSubject.next(DEFAULT_VALUE);
  }

  setStats(stats: AppStatsStore): void {
    this.statsSubject.next(stats);
  }

  refreshStats(silent): void {
    if(!silent) {
      this.statsSubject.next({ ...this.statsSubject.value , isLoading: true, isRefreshing: true});
    }
    const departmentId = this.statsSubject.value.departmentId;
    this.dashboardService
      .getDashboardStats({departmentId})
      .subscribe((res: DashboardStatsResponse) => {
        const alerts = res.alerts;
        const stats: Stats = {
          liveTests: res.stats.find((f) => f.id === 'live-tests')?.count || 0,
          totalPatients: res.stats.find((f) => f.id === 'patients')?.count || 0,
          testsToday:
            res.stats.find((f) => f.id === 'patients-scheduled-today')?.count ||
            0,
          deviceInInventory:
            res.stats.find((f) => f.id === 'inventory-devices')?.count || 0,
          deviceInInventoryLabel:
            res.stats.find((f) => f.id === 'inventory-devices')?.label || '',
          nightTechnicians:
            res.stats.find((f) => f.id === 'technicians')?.count || 0,
        };
        this.statsSubject.next({ ...this.statsSubject.value, alerts, stats , isLoading: false, isRefreshing: false});
      });
  }

  getStatsSnapshot(): AppStatsStore {
    return this.statsSubject.value;
  }

  clear(): void {
    this.statsSubject.next(DEFAULT_VALUE);
  }

  setDepartment(departmentId: number | null): void {
    this.statsSubject.next({
      ...this.statsSubject.value,
      departmentId,
    });
  }
}
